import {
  useUserProfile,
  useWbSidebarPinState,
  WbUserAvatar,
} from '@agilelab/plugin-wb-platform';
import { Link } from '@backstage/core-components';
import { makeStyles, Typography } from '@material-ui/core';
import { WbTooltip } from '../../WbTooltip';
import clsx from 'clsx';
import { useContext } from 'react';
import { SidebarConfig, WbSidebarConfigContext } from '../config';
import { useWbSidebarOpenState } from '../WbSidebarOpenStateContext';
import { useWbSidebarMobileState } from '../WbNavbarMobileStateContext';

const useStyle = makeStyles(theme => ({
  root: (props: SidebarConfig) => ({
    display: 'flex',
    alignItems: 'center',
    height: '64px',
    backgroundColor: theme.palette.grey[50],
    paddingInline: props.userBadgePadding,
    paddingBlock: '12px',
    gap: '8px',
  }),
  drawerWidth: (props: SidebarConfig) => ({
    width: props.drawerWidthClosed,
  }),
  drawerOpen: (props: SidebarConfig) => ({
    width: props.drawerWidthOpen,
  }),
  // keep this as a function to override on mobile drawerOpen
  mobile: () => ({
    width: '100%',
    height: '50px',
  }),
  avatar: (props: SidebarConfig) => ({
    /**
     * TODO We target the div with the avatar class specifically to increase specificity
     * because otherwise in production builds mui class overrides this class for some reasons, why this happens?
     */
    'div&': {
      width: props.userBadgeDiameter,
      height: props.userBadgeDiameter,
    },
  }),
  displayName: {
    color: theme.palette.black,
    '-webkit-box-orient': 'vertical',
    '-webkit-line-clamp': 2,
    overflow: 'hidden',
    display: '-webkit-box',
    wordBreak: 'break-all',
  },
}));

type WbSidebarAvatarProps = { to: string };

export const WbSidebarAvatar = ({ to }: WbSidebarAvatarProps) => {
  const { isOpen } = useWbSidebarOpenState();
  const { isMobile, setMenuOpen } = useWbSidebarMobileState();
  const { isTemporarilyPinned } = useWbSidebarPinState();
  const { sidebarConfig } = useContext(WbSidebarConfigContext);

  const { displayName } = useUserProfile();

  const classes = useStyle(sidebarConfig);

  const handleClick = isMobile
    ? () => {
        setMenuOpen(false);
      }
    : undefined;

  return (
    <Link
      className={clsx(
        classes.root,
        classes.drawerWidth,
        isOpen && classes.drawerOpen,
        isMobile && classes.mobile,
      )}
      onClick={handleClick}
      underline="none"
      aria-label="Profile Settings"
      to={to}
      tabIndex={isTemporarilyPinned ? -1 : 0}
    >
      <WbUserAvatar className={classes.avatar} />
      {isOpen && (
        <WbTooltip title={displayName} placement="right">
          <Typography
            className={classes.displayName}
            variant="body1"
            component="span"
          >
            {displayName}
          </Typography>
        </WbTooltip>
      )}
    </Link>
  );
};
